import type { ReactNode } from 'react'

import { cn } from '@/lib/utils'

/** Canonical inset for filter dropdown bodies (unified picker, legacy chip popovers, sort). */
export const FILTER_DROPDOWN_BODY_CLASS = 'flex flex-col gap-0.5 p-1'

export type FilterDropdownBodyProps = {
  children: ReactNode
  className?: string
}

export function FilterDropdownBody({ children, className }: FilterDropdownBodyProps) {
  return <div className={cn(FILTER_DROPDOWN_BODY_CLASS, className)}>{children}</div>
}

export type FilterDropdownFooterProps = {
  children: ReactNode
  className?: string
}

/** Help text, coverage hints, and non-operator footers below the option list. */
export function FilterDropdownFooter({ children, className }: FilterDropdownFooterProps) {
  return (
    <div
      className={cn(
        'border-t border-border/60 px-2.5 py-2 text-xs text-muted-foreground',
        className,
      )}
    >
      {children}
    </div>
  )
}

export type FilterDropdownHintProps = {
  children: ReactNode
  hint?: ReactNode
  className?: string
}

/** Inline scoped-edit feedback (location / organisation) — same horizontal inset as footer. */
export function FilterDropdownHint({ children, hint, className }: FilterDropdownHintProps) {
  return (
    <div className={cn('space-y-0.5 p-1.5', className)}>
      <div className="px-2.5 py-2 text-sm text-muted-foreground">{children}</div>
      {hint ? (
        <div className="px-2.5 pb-1.5 text-xs italic text-muted-foreground">{hint}</div>
      ) : null}
    </div>
  )
}

export type FilterPopoverPanelProps = {
  children: ReactNode
  footer?: ReactNode
  className?: string
}

/**
 * Shared shell for legacy chip popovers and sort menus — no back header.
 * Wraps content in the canonical body padding; optional footer below.
 */
export function FilterPopoverPanel({ children, footer, className }: FilterPopoverPanelProps) {
  return (
    <div className={cn('flex flex-col', className)}>
      <FilterDropdownBody>{children}</FilterDropdownBody>
      {footer ? <FilterDropdownFooter>{footer}</FilterDropdownFooter> : null}
    </div>
  )
}
